/**
 * Lightweight on-screen debug logger.
 *
 * Why this exists:
 * The CometChat iframe is loaded inside Shopify, which on mobile is wrapped
 * in Kin's native webview. We can't easily attach Safari Web Inspector to
 * that webview, and console.log output is invisible to the user there.
 *
 * This module:
 *   1. Mirrors console.log / console.warn / console.error to an in-page panel
 *      (toggled by appending `?debug=1` to the iframe URL).
 *   2. Captures unhandled errors and unhandled promise rejections.
 *   3. Buffers messages even before the panel is mounted, so early boot
 *      errors aren't lost.
 *
 * Usage:
 *   - Add `?debug=1` to the iframe's src in Shopify (or just to the Netlify
 *     URL while testing) to see the panel.
 *   - The panel is fixed to the bottom of the viewport, scrollable, and
 *     can be dismissed with the close button.
 */

type LogLevel = "log" | "warn" | "error" | "info";

interface LogEntry {
  level: LogLevel;
  ts: number;
  text: string;
}

const buffer: LogEntry[] = [];
const MAX_ENTRIES = 200;
let panelEl: HTMLDivElement | null = null;
let listEl: HTMLDivElement | null = null;
let installed = false;

function isDebugEnabled(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.get("debug") === "1") return true;
    if (window.localStorage?.getItem("cometchat_debug") === "1") return true;
  } catch {
    // localStorage may throw in some iframe contexts
  }
  return false;
}

function fmt(args: unknown[]): string {
  return args
    .map((a) => {
      if (a instanceof Error) {
        return `${a.name}: ${a.message}${a.stack ? "\n" + a.stack : ""}`;
      }
      if (typeof a === "object") {
        try {
          return JSON.stringify(a, null, 2);
        } catch {
          return String(a);
        }
      }
      return String(a);
    })
    .join(" ");
}

function push(level: LogLevel, args: unknown[]): void {
  const entry: LogEntry = {
    level,
    ts: Date.now(),
    text: fmt(args),
  };
  buffer.push(entry);
  if (buffer.length > MAX_ENTRIES) buffer.shift();
  render(entry);
}

function color(level: LogLevel): string {
  switch (level) {
    case "error":
      return "#ff6b6b";
    case "warn":
      return "#ffb84d";
    case "info":
      return "#5dd0ff";
    default:
      return "#cfd8dc";
  }
}

function ensurePanel(): void {
  if (panelEl || !document.body) return;

  panelEl = document.createElement("div");
  panelEl.id = "cometchat-debug-panel";
  Object.assign(panelEl.style, {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    maxHeight: "45vh",
    background: "rgba(10, 10, 14, 0.92)",
    color: "#cfd8dc",
    fontFamily:
      "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
    fontSize: "11px",
    lineHeight: "1.4",
    zIndex: "2147483647",
    borderTop: "1px solid #444",
    display: "flex",
    flexDirection: "column",
  } as Partial<CSSStyleDeclaration>);

  const header = document.createElement("div");
  Object.assign(header.style, {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "6px 10px",
    background: "rgba(255,255,255,0.05)",
    borderBottom: "1px solid #333",
  } as Partial<CSSStyleDeclaration>);

  const title = document.createElement("span");
  title.textContent = "CometChat debug";
  title.style.fontWeight = "600";
  title.style.color = "#fff";

  const buttons = document.createElement("div");

  const copyBtn = document.createElement("button");
  copyBtn.textContent = "Copy";
  styleBtn(copyBtn);
  copyBtn.onclick = () => {
    const text = buffer
      .map((e) => `[${new Date(e.ts).toISOString()}] ${e.level.toUpperCase()} ${e.text}`)
      .join("\n");
    try {
      navigator.clipboard?.writeText(text);
      copyBtn.textContent = "Copied";
      setTimeout(() => (copyBtn.textContent = "Copy"), 1200);
    } catch {
      // Clipboard may not be available in iframe; fall back to selection.
      const ta = document.createElement("textarea");
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        /* ignore */
      }
      ta.remove();
    }
  };

  const clearBtn = document.createElement("button");
  clearBtn.textContent = "Clear";
  styleBtn(clearBtn);
  clearBtn.onclick = () => {
    buffer.length = 0;
    if (listEl) listEl.innerHTML = "";
  };

  const closeBtn = document.createElement("button");
  closeBtn.textContent = "×";
  styleBtn(closeBtn);
  closeBtn.style.fontSize = "16px";
  closeBtn.style.padding = "0 8px";
  closeBtn.onclick = () => {
    panelEl?.remove();
    panelEl = null;
    listEl = null;
  };

  buttons.appendChild(copyBtn);
  buttons.appendChild(clearBtn);
  buttons.appendChild(closeBtn);

  header.appendChild(title);
  header.appendChild(buttons);

  listEl = document.createElement("div");
  Object.assign(listEl.style, {
    overflowY: "auto",
    padding: "6px 10px",
    flex: "1",
    WebkitOverflowScrolling: "touch",
  } as Partial<CSSStyleDeclaration>);

  panelEl.appendChild(header);
  panelEl.appendChild(listEl);
  document.body.appendChild(panelEl);

  // Replay buffered entries
  buffer.forEach((e) => render(e));
}

function styleBtn(btn: HTMLButtonElement): void {
  Object.assign(btn.style, {
    background: "transparent",
    border: "1px solid #555",
    color: "#fff",
    fontSize: "11px",
    padding: "2px 8px",
    marginLeft: "6px",
    borderRadius: "4px",
    cursor: "pointer",
  } as Partial<CSSStyleDeclaration>);
}

function render(entry: LogEntry): void {
  if (!isDebugEnabled()) return;
  ensurePanel();
  if (!listEl) return;

  const row = document.createElement("div");
  row.style.borderBottom = "1px dashed rgba(255,255,255,0.06)";
  row.style.padding = "2px 0";
  row.style.color = color(entry.level);
  row.style.whiteSpace = "pre-wrap";
  row.style.wordBreak = "break-word";

  const t = new Date(entry.ts);
  const stamp = `${pad(t.getHours())}:${pad(t.getMinutes())}:${pad(t.getSeconds())}.${pad3(t.getMilliseconds())}`;

  row.textContent = `[${stamp}] ${entry.level.toUpperCase()} ${entry.text}`;
  listEl.appendChild(row);
  listEl.scrollTop = listEl.scrollHeight;
}

function pad(n: number): string {
  return n < 10 ? "0" + n : "" + n;
}
function pad3(n: number): string {
  if (n < 10) return "00" + n;
  if (n < 100) return "0" + n;
  return "" + n;
}

/**
 * Wrap window.fetch and window.WebSocket so every network call CometChat
 * makes shows up in the debug panel.
 *
 * Why: when chat content hangs (skeleton loaders forever, conversation
 * list stuck), the SDK frequently doesn't surface an error — the
 * request just sits open. Application-level logs can't see this; only
 * intercepting the network layer can. We log:
 *   - fetch:  URL, method, status code, duration. Filtered to CometChat
 *             hosts so we don't drown in unrelated traffic.
 *   - WebSocket: open / message-count-on-close / error / close-code.
 *
 * Cheap to leave installed in production — the panel is hidden unless
 * `?debug=1` is set, and the buffer caps at MAX_ENTRIES.
 */
function isCometChatUrl(url: string): boolean {
  // CometChat APIs live under cometchat.io and a few CDN subdomains.
  // Match broadly so we don't miss anything important.
  return /cometchat\.io|cometchat\.com|cometchat\.cloud/i.test(url);
}

/**
 * 401-watcher state.
 *
 * When a CometChat API returns 401, the most likely cause is that the
 * user was deleted from the dashboard (or the auth token was rotated)
 * but the browser still has the stale token in localStorage. In that
 * state every fetch fails and the chat looks "blank but working" —
 * shell renders, no content loads.
 *
 * We dispatch a `cometchat-auth-invalid` window event the first time we
 * observe a 401. App.tsx listens for it, clears the cometchat
 * localStorage keys, and re-runs the login flow, which then hits
 * ERR_UID_NOT_FOUND and re-registers the user fresh.
 *
 * `authInvalidDispatched` debounces — a single fetch that 401s often
 * causes a cascade of follow-up 401s on every other in-flight request,
 * and we only need to dispatch the recovery event once per page load.
 */
let authInvalidDispatched = false;

function dispatchAuthInvalid(reason: string): void {
  if (authInvalidDispatched) return;
  authInvalidDispatched = true;
  push("warn", [`CometChat auth invalid: ${reason}. Triggering recovery.`]);
  try {
    window.dispatchEvent(
      new CustomEvent("cometchat-auth-invalid", { detail: { reason } }),
    );
  } catch {
    /* CustomEvent should always work in modern browsers */
  }
}

function installFetchHook(): void {
  const origFetch = window.fetch?.bind(window);
  if (!origFetch) return;

  window.fetch = async (
    input: RequestInfo | URL,
    init?: RequestInit,
  ): Promise<Response> => {
    let url: string;
    let method = "GET";
    if (typeof input === "string") {
      url = input;
    } else if (input instanceof URL) {
      url = input.toString();
    } else {
      url = input.url;
      method = input.method || "GET";
    }
    if (init?.method) method = init.method;

    if (!isCometChatUrl(url)) {
      return origFetch(input, init);
    }

    const start = Date.now();
    const short = url.replace(/^https?:\/\/[^/]+/, "");
    push("log", [`fetch START ${method} ${short}`]);
    try {
      const res = await origFetch(input, init);
      const dur = Date.now() - start;
      push(res.ok ? "log" : "warn", [
        `fetch ${res.status} ${method} ${short} (${dur}ms)`,
      ]);
      // Treat 401 (Unauthorized) as a stale-token signal. We deliberately
      // do *not* fire on 403 — that's "you don't have permission to do
      // this thing" rather than "you aren't who you say you are".
      if (res.status === 401) {
        dispatchAuthInvalid(`401 on ${method} ${short}`);
      }
      return res;
    } catch (err) {
      const dur = Date.now() - start;
      push("error", [
        `fetch FAIL ${method} ${short} (${dur}ms):`,
        err,
      ]);
      throw err;
    }
  };
}

/**
 * Watch document.body and document.documentElement for injected elements
 * during the first 8 seconds of page load. Used to track down "what is
 * the SDK adding to the page that I can't catch with right-click
 * Inspect because it disappears too fast?"
 *
 * Logs both insertions AND removals so we can correlate "X appeared at
 * t=200ms, was removed at t=2400ms" — useful for identifying transient
 * elements like the connection-phase chat bubble.
 *
 * Filtered to elements that are likely floating UI: anything with
 * `position: fixed` / `position: absolute`, or anything whose root
 * id/class contains `cometchat`/`cc-`/`chat-`. Plain layout elements
 * (divs added by React, stylesheets injected by the SDK, etc.) are
 * skipped to keep the log readable.
 */
function installDomInjectionLogger(): void {
  if (typeof MutationObserver !== "function") return;

  const getClassString = (node: Element): string => {
    const rawCls: unknown = (node as { className?: unknown }).className;
    if (typeof rawCls === "string") return rawCls;
    if (
      rawCls &&
      typeof rawCls === "object" &&
      typeof (rawCls as { baseVal?: unknown }).baseVal === "string"
    ) {
      return (rawCls as { baseVal: string }).baseVal;
    }
    return "";
  };

  const isInteresting = (node: Node): node is Element => {
    if (!(node instanceof Element)) return false;
    if (node.tagName === "STYLE" || node.tagName === "SCRIPT") return false;
    if (node.tagName === "LINK" || node.tagName === "META") return false;
    // SVG elements are caught at any level — they're commonly used for
    // icons and the floating bubble we're chasing is almost certainly
    // one. (Previously we only matched HTMLElement and silently
    // skipped SVGs.)
    if (node.tagName === "svg" || node.tagName === "SVG") return true;
    const id = node.id?.toLowerCase() ?? "";
    const cls = getClassString(node).toLowerCase();
    const keywords = [
      "cometchat",
      "cc-",
      "chat",
      "bubble",
      "launcher",
      "fab",
      "floating",
      "popup",
    ];
    if (keywords.some((k) => id.includes(k) || cls.includes(k))) return true;
    return false;
  };

  const describe = (node: Element): string => {
    const id = node.id ? `#${node.id}` : "";
    const clsString = getClassString(node);
    const cls = clsString
      ? `.${clsString.split(/\s+/).filter(Boolean).join(".")}`
      : "";
    const html = node.outerHTML.slice(0, 500);
    return `<${node.tagName.toLowerCase()}${id}${cls}> :: ${html}${node.outerHTML.length > 500 ? "..." : ""}`;
  };

  const start = () => {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (isInteresting(n)) {
            push("info", [`DOM ADDED: ${describe(n as HTMLElement)}`]);
          }
        });
        m.removedNodes.forEach((n) => {
          if (isInteresting(n)) {
            push("info", [`DOM REMOVED: ${describe(n as HTMLElement)}`]);
          }
        });
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    // Stop logging after 8 seconds — long enough to capture the
    // first-load connection phase, short enough not to flood the
    // panel during normal use.
    setTimeout(() => observer.disconnect(), 8000);
  };

  if (document.body) {
    start();
  } else {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  }
}

function installWebSocketHook(): void {
  const OrigWS = window.WebSocket;
  if (!OrigWS) return;

  // Subclassing the native WebSocket constructor so connections opened
  // by the CometChat SDK get logged too. The proxy preserves the
  // original prototype and constants (CONNECTING / OPEN / CLOSING /
  // CLOSED) for code that reads them off the constructor.
  const Wrapped = function (this: WebSocket, url: string | URL, protocols?: string | string[]) {
    const ws = new OrigWS(url, protocols);
    const urlStr = typeof url === "string" ? url : url.toString();
    if (isCometChatUrl(urlStr)) {
      push("log", [`WS connecting ${urlStr}`]);
      ws.addEventListener("open", () => {
        push("info", [`WS open ${urlStr}`]);
      });
      ws.addEventListener("error", (e: Event) => {
        push("error", [`WS error ${urlStr}`, e]);
      });
      ws.addEventListener("close", (e: CloseEvent) => {
        push("warn", [
          `WS close ${urlStr} code=${e.code} reason="${e.reason || ""}" wasClean=${e.wasClean}`,
        ]);
      });
    }
    return ws;
  } as unknown as typeof WebSocket;
  Wrapped.prototype = OrigWS.prototype;
  // Copy static constants (CONNECTING/OPEN/CLOSING/CLOSED).
  (Wrapped as unknown as { CONNECTING: number }).CONNECTING = OrigWS.CONNECTING;
  (Wrapped as unknown as { OPEN: number }).OPEN = OrigWS.OPEN;
  (Wrapped as unknown as { CLOSING: number }).CLOSING = OrigWS.CLOSING;
  (Wrapped as unknown as { CLOSED: number }).CLOSED = OrigWS.CLOSED;
  window.WebSocket = Wrapped;
}

/**
 * Install console hooks + global error handlers + network interceptors.
 * Safe to call more than once.
 */
export function installDebugLog(): void {
  if (installed) return;
  installed = true;

  const origLog = console.log.bind(console);
  const origInfo = console.info ? console.info.bind(console) : origLog;
  const origWarn = console.warn.bind(console);
  const origError = console.error.bind(console);

  console.log = (...args: unknown[]) => {
    push("log", args);
    origLog(...args);
  };
  console.info = (...args: unknown[]) => {
    push("info", args);
    origInfo(...args);
  };
  console.warn = (...args: unknown[]) => {
    push("warn", args);
    origWarn(...args);
  };
  console.error = (...args: unknown[]) => {
    push("error", args);
    origError(...args);
  };

  window.addEventListener("error", (e) => {
    push("error", [
      `window.error: ${e.message}`,
      e.filename ? `(${e.filename}:${e.lineno}:${e.colno})` : "",
    ]);
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason = e.reason;
    push("error", ["unhandledrejection:", reason]);
  });

  installFetchHook();
  installWebSocketHook();
  installDomInjectionLogger();

  // Mount the panel as soon as the body is available.
  if (document.body) {
    if (isDebugEnabled()) ensurePanel();
  } else {
    document.addEventListener("DOMContentLoaded", () => {
      if (isDebugEnabled()) ensurePanel();
    });
  }

  push("info", [
    "Debug log installed.",
    `userAgent=${navigator.userAgent}`,
    `inIframe=${window.top !== window.self}`,
    `cookieEnabled=${navigator.cookieEnabled}`,
    `referrer=${document.referrer}`,
  ]);
}

/**
 * Direct logger for code paths that want to record a structured event,
 * even if the user hasn't opted into the on-screen panel.
 */
export const dlog = {
  log: (...args: unknown[]) => push("log", args),
  info: (...args: unknown[]) => push("info", args),
  warn: (...args: unknown[]) => push("warn", args),
  error: (...args: unknown[]) => push("error", args),
};
